import React from 'react';
import { motion } from 'framer-motion';

interface StepNavigationProps {
  steps: string[];
  activeStep: number;
  skipCompany?: boolean;
  onStepClick?: (step: number) => void;
  completedSteps?: boolean[];
}

const StepNavigation: React.FC<StepNavigationProps> = ({ steps, activeStep, skipCompany, onStepClick, completedSteps }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const activeEl = scrollRef.current?.querySelector(`[data-active="true"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeStep]);

  return (
    <div className="relative rounded-t-xl px-2 sm:px-8 py-4 shadow-lg w-full overflow-hidden" style={{ backgroundColor: '#010D2C' }}>
      <div
        ref={scrollRef}
        className="flex items-center justify-between gap-1 overflow-x-auto no-scrollbar scroll-smooth relative z-10 py-2"
      >
        {steps.map((label, idx) => {
          const isActive = idx === activeStep;
          const isCompleted = completedSteps ? completedSteps[idx] : idx < activeStep;

          return (
            <div
              key={idx}
              data-active={isActive}
              onClick={() => onStepClick?.(idx)}
              className={`relative flex flex-col items-center transition-all duration-500 ease-in-out ${onStepClick ? 'cursor-pointer' : 'cursor-default'
                } ${isActive
                  ? 'flex-[2_0_auto] min-w-[100px] opacity-100'
                  : 'flex-1 min-w-[40px] opacity-40 hover:opacity-70'
                }`}
            >
              <div className="flex flex-col items-center gap-1.5 cursor-default w-full">
                <div
                  className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold transition-all duration-300 ${isActive
                    ? 'bg-white text-[#010D2C] shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                    : isCompleted
                      ? 'bg-green-500 text-white'
                      : 'bg-white/10 text-white/40'
                    }`}
                >
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`text-[10px] sm:text-xs font-semibold tracking-wide uppercase transition-all duration-300 px-1 text-center w-full truncate ${isActive ? 'text-white' : 'text-white/0 lg:text-white/40'
                    } ${isActive ? 'block' : 'hidden lg:block'}`}
                >
                  {label}
                </span>
              </div>

              {isActive && (
                <motion.div
                  layoutId="active-pill"
                  className="absolute -inset-y-1 -inset-x-2 bg-white/5 rounded-xl -z-10"
                  initial={false}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Subtle Progress Line at the very bottom */}
      <div className="absolute bottom-0 left-0 h-1 bg-white/5 w-full">
        <motion.div
          className="h-full bg-green-500"
          initial={{ width: '0%' }}
          animate={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
        />
      </div>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default StepNavigation;
